import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { DEFAULT_THEME_COLOR } from 'appenv';
import { withStyles } from '@material-ui/core/styles';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import SwipeableViews from 'react-swipeable-views';
import { Booth } from 'models/cms/booths';
import { useSelector } from 'react-redux';
import ProductTab from 'components/product/ProductTab';
import { selectLocale } from 'models/localization';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import { selectTierNameById } from 'models/cms/boothTiers/selectors';
import { selectBoothProductsByBoothId } from 'models/boothProducts';
import regexUtil from 'utils/regexUtil';
import isProductsTab from 'utils/isProductsTab';
import { TabsContext } from 'components/TabsContext';
import TabContentSwitcher from './TabContentSwitcher';

const Container = styled.div`
  width: 100%;
  height: auto;
  margin: 20px auto;
  box-shadow: 0 4px 40px rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  background-color: #FFF;
  letter-spacing: 0.3px;

  & .MuiTab-wrapper {
    font-size: 18px;
  }

  & button.MuiButtonBase-root.MuiTab-root {
    margin-right: 20px;
  }

  @media (min-width: 960px) {
    width: 70%;

    .MuiTab-wrapper {
      font-size: 24px;
    }

    button.MuiButtonBase-root.MuiTab-root {
      margin-right: 40px;
    }
  }
`;

const StyledTabs = ({ children, ...props }: any) => {
  const { themeColor } = useSelector((s: any) => s.eventConfigs);
  const Custom = withStyles({
    root: {
      padding: 24,
    },
    indicator: {
      display: 'flex',
      justifyContent: 'center',
      backgroundColor: 'transparent',
      height: '4px',
      '& > div': {
        maxWidth: 139,
        width: '100%',
        backgroundColor: themeColor || DEFAULT_THEME_COLOR,
      },
    },
    scrollable: {
      overflowX: 'auto',
    },
  })(Tabs);
  return <Custom {...props as any} TabIndicatorProps={{ children: <div /> }}>{children as any}</Custom>;
};

const StyledTab = ({ children, ...props }: any) => {
  const { themeColor } = useSelector((s: any) => s.eventConfigs);
  const Custom = withStyles((theme) => ({
    root: {
      textTransform: 'none',
      color: '#A2AAB3',
      fontWeight: theme.typography.fontWeightBold,
      fontSize: theme.typography.pxToRem(24),
      fontFamily: 'Lato',
      padding: '0',
      minWidth: 'unset',
      whiteSpace: 'pre-line',
      '&:focus': {
        opacity: 1,
      },
    },
    selected: {
      color: themeColor || DEFAULT_THEME_COLOR,
    },
    wrapper: {
      display: 'block',
    },
  }))(Tab);
  return <Custom {...props as any} disableRipple>{children as any}</Custom>;
};

const TabPanelContainer = styled.div`
  padding-bottom: 20px;
`;

const TabPanel = ({ children, value, index }) => (
  <TabPanelContainer
    role="tabpanel"
    hidden={value !== index}
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
  >
    {value === index && (
      children
    )}
  </TabPanelContainer>
);

TabPanel.propTypes = {
  children: PropTypes.node.isRequired,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
};

const BoothTierLabel = styled.p`
  display: inline-block;
  margin: 0 0 24px;
  transform: translate(24px, 36px);
  background: #FFCB05;
  font-size: 16px;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.18);
  border-radius: 6px;
  padding: 0.25rem 1rem;
  max-width: calc(100% - 48px);
  word-break: break-word;
`;

const isProductTab = (tab) => Object.values(tab.title).some(isProductsTab);

const BoothProfileTabs = ({ booth }: {
  booth: Booth;
}) => {
  const locale = useSelector(selectLocale);
  const { features, tabRegex } = useSelector((s: any) => s.eventConfigs);
  const { activeTabKey, setActiveTabKey, tabs } = useContext(TabsContext);
  const boothName = findAvailableLocale(booth?.name || {}, 'en');
  const tierName = useSelector((state) => selectTierNameById(state, booth?.tier?.id));
  const boothProducts = useSelector(selectBoothProductsByBoothId(booth?.id));
  const fetchingProducts = useSelector((state: any) => state.boothProducts.fetching);

  const onActiveTabKeyChange = (event: React.ChangeEvent<HTMLSpanElement>, tabKey: number) => {
    window.trackingItemToFirebase(null, 'Booth', 'Booth_Profile_Tab', 'Tab', tabKey + 1, booth.id, boothName);
    setActiveTabKey(tabKey);
  };

  const handleLinkClink = (event) => {
    if (event?.target?.tagName === 'A') {
      event.target.target = '_blank';
      event.target.rel = 'noopener noreferrer';
    }
  };

  if (tabs.length === 0) return null;
  const tabNameRegex = regexUtil(tabRegex);
  const getLabel = (title) => {
    if (features.includes('bilingualTab') && !!tabNameRegex) {
      const matched = title?.match(tabNameRegex);
      if (!matched) return title;
      return `${title.slice(0, matched.index)}
        ${title.slice(matched.index)}`;
    }
    return title;
  };

  return (
    <Container>
      {features.includes('boothTiers') && tierName?.[locale] && <BoothTierLabel>{tierName[locale]}</BoothTierLabel>}
      <StyledTabs scrollButtons="off" variant="scrollable" value={activeTabKey} onChange={onActiveTabKeyChange} aria-label="Booth Details">
        {tabs.map((tab, index) => (
          <StyledTab key={index} label={getLabel(findAvailableLocale(tab.title, locale))} aria-label={findAvailableLocale(tab.title, locale)} />
        ))}
      </StyledTabs>
      <SwipeableViews
        axis="x"
        index={activeTabKey}
        disabled
        ignoreNativeScroll
        resistance
        onClick={handleLinkClink}
      >
        {tabs.map((tab, index) => (
          <TabPanel key={index} value={activeTabKey} index={index}>
            {isProductTab(tab) && features.includes('productPage') ? (
              <ProductTab boothProducts={boothProducts} fetching={fetchingProducts} />
            ) : (
              <TabContentSwitcher key={tab.title[locale]} contents={tab.body} />
            )}
          </TabPanel>
        ))}
      </SwipeableViews>
    </Container>
  );
};

export default React.memo(BoothProfileTabs);
